// ============================================================================
// telemetry.js — how the portal is used, from the browser: the visit, each
// page shown and how long it was actually used, and named actions. The server
// side is backend/src/services/usageAnalytics.js; what the numbers mean is in
// database/migrations/096_usage_analytics.sql.
//
//   visit     an id kept in sessionStorage; a new one after 30 minutes with no
//             input, after sign-out, or in a new tab
//   page      a row per page shown (viewId), re-sent every 30 seconds with its
//             engaged and elapsed time, and a last time when the page is left
//   engaged   time the tab was visible and focused with an input in the last
//             five minutes; a page left open behind another window does not
//             accumulate
//
// First-party only (the CSP admits no third-party script), nothing sent
// before sign-in, and no typed values: an action is a name from a closed list,
// never what was entered. Sending uses fetch with keepalive, so the last batch
// survives the tab closing, and carries the token like any other API call.
// Every failure is swallowed: telemetry must never break a page.
// ============================================================================

const IDLE_MS = 30 * 60 * 1000;          // a new visit after this long with no input
const ENGAGED_WINDOW_MS = 5 * 60 * 1000; // input this recent counts as in use
const TICK_MS = 5 * 1000;
const FLUSH_MS = 30 * 1000;
const MAX_ERRORS_PER_PAGE = 10;
const KEY = 'ux_session';

const uuid = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  }));

/**
 * The tracker, with everything it touches passed in so it can be tested with a
 * fake clock: `now()`, `storage` (sessionStorage), `send(batch)` and `doc`
 * (visibility and focus).
 */
export function createTelemetry({ now = () => Date.now(), storage, send, doc }) {
  let queue = [];
  let page = null;               // { viewId, route, fromRoute, startedAt, engagedMs, errors }
  let lastInput = now();
  let lastTick = now();

  function readSession() {
    try { return JSON.parse(storage.getItem(KEY) || 'null'); } catch { return null; }
  }
  function writeSession(s) {
    try { storage.setItem(KEY, JSON.stringify(s)); } catch { /* storage full or blocked: carry on */ }
  }

  /** The current visit's id, starting a new visit when there is none or it went idle. */
  function session(route) {
    const t = now();
    let s = readSession();
    if (!s || !s.id || t - (s.lastActivity || 0) > IDLE_MS) {
      s = { id: uuid(), lastActivity: t };
      queue.push({ type: 'session_start', at: t, route });
    }
    s.lastActivity = t;
    writeSession(s);
    return s.id;
  }

  function inView() {
    const visible = !doc || doc.visibilityState === 'visible';
    const focused = !doc || typeof doc.hasFocus !== 'function' || doc.hasFocus();
    return visible && focused;
  }

  /**
   * Count the time since the last tick that was engaged: in view, and before
   * the five minutes after the last input ran out — to the millisecond, so a
   * tick straddling that moment counts the part before it.
   */
  function tick() {
    const t = now();
    const from = Math.max(lastTick, t - TICK_MS * 3);                  // a sleeping laptop is not a long read
    lastTick = t;
    if (!page || !inView()) return;
    const until = Math.min(t, lastInput + ENGAGED_WINDOW_MS);
    if (until > from) page.engagedMs += until - from;
  }

  function pageEvent(ended) {
    return {
      type: 'page_view', at: now(), viewId: page.viewId, route: page.route, fromRoute: page.fromRoute,
      engagedMs: Math.round(page.engagedMs), durationMs: now() - page.startedAt, ended,
    };
  }

  async function flush({ final = false } = {}) {
    tick();
    if (page) queue.push(pageEvent(final));
    if (!queue.length) return;
    const s = readSession();
    if (!s?.id) { queue = []; return; }
    const batch = { sessionId: s.id, events: queue.splice(0, 50) };
    try { await send(batch, { final }); } catch { /* telemetry never breaks a page */ }
    if (queue.length) await flush({ final });
  }

  return {
    /** A page is shown: end the one before, start this one. */
    page(path) {
      tick();
      const fromRoute = page?.route ?? null;
      if (page) queue.push(pageEvent(true));
      session(path);
      page = { viewId: uuid(), route: path, fromRoute, startedAt: now(), engagedMs: 0, errors: 0 };
      lastInput = now();                                   // navigating is an input
      queue.push(pageEvent(false));
    },
    /** Any user input: keeps the visit alive and the page engaged. */
    input() {
      tick();
      lastInput = now();
      const s = readSession();
      if (s?.id) { s.lastActivity = now(); writeSession(s); }
    },
    /** A named action from the allow-list (server refuses anything else). */
    track(name, props = {}) {
      queue.push({ type: 'feature', at: now(), name, route: page?.route ?? null, ...props });
    },
    error(message, source) {
      if (page && page.errors >= MAX_ERRORS_PER_PAGE) return;
      if (page) page.errors++;
      queue.push({ type: 'client_error', at: now(), route: page?.route ?? null, message: String(message || ''), source });
    },
    tick,
    flush,
    /** Sign-out: close the page and the visit, and forget the visit id. */
    async signOut() {
      tick();
      if (page) queue.push(pageEvent(true));
      page = null;
      queue.push({ type: 'sign_out', at: now() });
      await flush({ final: true });
      try { storage.removeItem(KEY); } catch { /* ignore */ }
    },
    // For tests.
    _state: () => ({ queue: [...queue], page: page && { ...page }, session: readSession() }),
  };
}

// ── The app's one tracker ────────────────────────────────────────────────────

const BASE_URL = (import.meta.env && import.meta.env.VITE_API_BASE_URL) || '';

function sendBatch(batch) {
  const token = localStorage.getItem('mor_token');
  if (!token) return Promise.resolve();                   // nothing before sign-in
  return fetch(`${BASE_URL}/api/telemetry/events`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(batch),
  }).catch(() => {});
}

let tracker = null;
let started = false;

function get() {
  if (!tracker && typeof window !== 'undefined') {
    tracker = createTelemetry({ storage: window.sessionStorage, send: sendBatch, doc: document });
  }
  return tracker;
}

/** Start listening: inputs, the engagement clock, periodic flushes, page hide, errors. */
export function startTelemetry() {
  const t = get();
  if (!t || started) return;
  started = true;
  let lastInputAt = 0;
  const onInput = () => {
    const n = Date.now();
    if (n - lastInputAt > 1000) { lastInputAt = n; t.input(); }   // throttled: mousemove fires constantly
  };
  for (const ev of ['pointerdown', 'keydown', 'wheel', 'mousemove', 'touchstart', 'scroll']) {
    window.addEventListener(ev, onInput, { passive: true, capture: true });
  }
  // unref: in a browser these are plain numbers and nothing changes; under
  // Node (the tests mount AppShell) a live interval would keep the process up.
  setInterval(() => t.tick(), TICK_MS)?.unref?.();
  setInterval(() => { t.flush(); }, FLUSH_MS)?.unref?.();
  document.addEventListener('visibilitychange', () => {
    t.tick();
    if (document.visibilityState === 'hidden') t.flush();
  });
  window.addEventListener('pagehide', () => { t.flush({ final: true }); });
  window.addEventListener('error', (e) => t.error(e.message, window.location.pathname));
  window.addEventListener('unhandledrejection', (e) => t.error(e.reason?.message || String(e.reason || ''), window.location.pathname));
}

export const trackPage = (path) => get()?.page(path);
export const trackAction = (name, props) => get()?.track(name, props);
export const telemetrySignOut = () => get()?.signOut() ?? Promise.resolve();
